import get from 'lodash/get'
import Moment from 'moment'
import * as Redux from 'redux'
import * as DetailPanel from '../../components/detail-panel'
import * as Form from '../../components/form'
import * as IsActive from '../../components/is_active'
import * as Modal from '../../components/modal'
import * as Pager from '../../components/pager'
import * as Request from '../../request'
import * as Router from '../../router'
import * as Actions from './actions'
import * as AddModal from './add-modal'
import * as AttributeTypes from './attributes/types'
import * as Constants from './constants'
import * as MemberDetails from './details/constants'
import * as Types from './types'
import * as Utils from './utils'

const payloadReducer = <T>(attribute: string) =>
  (state: T[] = [], action: Request.SuccessAction) => {
    switch (action.type) {
      case Request.errorConstant(Constants.FETCH_MEMBERS):
        return []
      case Request.successConstant(Constants.FETCH_MEMBERS):
        return action.payload.response ? get(action.payload.response, attribute) || [] : state
      default:
        return state
    }
  }

export const membersReducer = (state: Types.Member[] = [], action: Request.SuccessAction) => {
  switch (action.type) {
    case Request.requestConstant(Constants.FETCH_MEMBERS):
    case Request.requestConstant(Constants.FETCH_MEMBER):
      return []
    case Request.successConstant(Constants.FETCH_MEMBER):
    case Request.successConstant(Constants.FETCH_MEMBERS):
      return action.payload.response.results.map((r: any) => normalizeMember(r.data))
    default:
      return state
  }
}

const normalizeMember = (payload: Types.Member): Types.Member => {
  return {
    ...payload,
    created_datetime: Moment(payload.created),
    updated_datetime: Moment(payload.updated),
  }
}

export const emptyMetaData = {
  errors: '',
  next_link: '',
  record_count: 0,
  request: '',
  timestamp: '',
  token_expiration: '',
  total_query_count: 0,
}

export const metaDataReducer = (state: Types.MetaData = emptyMetaData, action: Request.SuccessAction) => {
  switch (action.type) {
    case Request.successConstant(Constants.FETCH_MEMBER):
      return {
        ...state,
        record_count: 1,
        total_query_count: 1,
      }
    case Request.requestConstant(Constants.FETCH_MEMBERS):
      return {
        ...state,
        record_count: 0,
      }
    case Request.requestConstant(Constants.FETCH_MEMBER_COUNTS):
      return {
        ...state,
        total_query_count: 0,
      }
    case Request.successConstant(Constants.FETCH_MEMBERS):
      return {
        ...state,
        next_link: action.payload.response.metadata.next_link,
        record_count: action.payload.response.metadata.record_count,
      }
    case Request.successConstant(Constants.FETCH_MEMBER_COUNTS):
      return {
        ...state,
        total_query_count: action.payload.response.metadata.total_query_count,
      }
    default:
      return state
  }
}

const initialSearchState: Types.SearchState = {
  found: 0,
  index: 0,
  isSearching: false,
  results: [],
}

type SearchTypes = Request.SuccessAction & Request.SearchAction
export const searchReducer = (state: Types.SearchState = initialSearchState, action: SearchTypes) => {
  switch (action.type) {
    case Constants.ON_SEARCH:
      return {
        ...initialSearchState,
        index: action.index,
        isSearching: true,
      }

    default:
      return state
  }
}

const initialExportState = {
  isExportLoading: false,
  isLoading: false,
  sortBy: {
    direction: Constants.SORT_BY.Desc,
    field: Constants.MemberFields.Updated,
  },
}
type MembersViewAction =
  Request.SuccessAction &
  Router.OnEnterAction &
  Actions.FetchMembersAction &
  Actions.SortMembersAction
export const membersViewReducer =
(state: Types.ViewState = initialExportState, action: MembersViewAction) => {
  switch (action.type) {
    case Constants.SORT_MEMBERS:
      return {
        ...state,
        sortBy: {
          direction:
            (state.sortBy.field === action.field)
              ? (state.sortBy.direction === Constants.SORT_BY.Asc ? Constants.SORT_BY.Desc : Constants.SORT_BY.Asc)
              : Utils.defaultSort(action.field),
          field: action.field,
        },
      }
    case Request.requestConstant(Constants.FETCH_MEMBER):
    case Request.requestConstant(Constants.FETCH_MEMBERS):
        return {
        ...state,
        isLoading: true,
        sortBy: Object.keys(get(action, 'options.params', '')).includes('sort') ?
          {
            direction: state.sortBy.direction,
            field: action.options.params.sort,
          } : state.sortBy,
      }
    case Request.successConstant(Constants.FETCH_MEMBERS):
    case Request.errorConstant(Constants.FETCH_MEMBERS):
    case Request.successConstant(Constants.FETCH_MEMBER):
    case Request.errorConstant(Constants.FETCH_MEMBER):
      return {
        ...state,
        isLoading: false,
      }
    case Request.requestConstant(Constants.EXPORT_SUBSCRIBERS):
      return {
        ...state,
        isExportLoading: true,
      }
    case Request.successConstant(Constants.EXPORT_SUBSCRIBERS):
    case Request.errorConstant(Constants.EXPORT_SUBSCRIBERS):
      return {
        ...state,
        isExportLoading: false,
      }
    default:
      return state
  }
}

const modalReducerEvent: Modal.ReducerEvent<Types.ModalData> = {
  initializeState: () => Constants.emptyModalState,
  requestEvent: Constants.MEMBER_IMPORT,
}

const memberModalReducer = (sourceReducer: any) =>
  (state: Modal.State<Types.ModalData>, action: Modal.UpdateFieldAction) => {
  const sourceState = sourceReducer(state, action)
  switch (action.type) {
    case Modal.toggleModalConstant(Constants.MEMBER_IMPORT):
      return {
        ...sourceState,
        data: Constants.emptyModalState,
      }
    default:
      return sourceState
  }
}

type RowClick = DetailPanel.DetailPanelAction<Types.Member>
type MemberDetailActions = Request.SuccessAction & RowClick
const memberDetailReducer = (state: Types.MemberDetail = null, action: MemberDetailActions) => {
  switch (action.type) {
    case MemberDetails.DetailPanelConstants[DetailPanel.OPEN]:
      return {
        ...state,
        ...action.value,
      }
    case Request.successConstant(Constants.FETCH_MEMBER_DETAILS):
      return {
        ...state,
        memberAttributes: action.payload.response.results.map((result: any, index: number) => ({
          ...result,
          index,
        })),
        metadata: action.payload.response.metadata,
      }
    case Request.requestConstant(Constants.FETCH_MEMBERS):
      return {
      }
    default:
      return state
  }
}

export const reducer = Redux.combineReducers({
  addMemberModal: AddModal.reducer,
  attributes: payloadReducer<AttributeTypes.Attribute>('attributes'),
  data: IsActive.chainedReducer(membersReducer, Constants.UPDATE_MEMBER_INLINE),
  detailPanel: DetailPanel.createReducer(MemberDetails.MEMBER_DETAIL_PREFIX),
  filterForm: Form.createReducer(
    Constants.MEMBERS_FILTERS,
    Constants.MEMBERS_FILTER_FORM_INITIAL_VALUES,
  ),
  memberDetail: memberDetailReducer,
  metaData: metaDataReducer,
  modal: memberModalReducer(Modal.reducer(modalReducerEvent)),
  pager: Pager.createReducer(Constants.FETCH_MEMBERS),
  search: searchReducer,
  view: membersViewReducer,
})



// WEBPACK FOOTER //
// ./src/pages/members/reducer.ts